import {Injectable, Injector} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _assignIn from 'lodash/assignIn';
import {flatMap, forkJoin, from, Observable} from 'rxjs';
import Swal from 'sweetalert2';
import {map} from 'rxjs/operators';

/**
 *
 * For sweetalert2.noop visit : https://github.com/limonte/sweetalert2#handling-dismissals
 *
 */
@Injectable()
export class SwalService {
	public static empty: string = 'empty';

	public static success(title: string, description: string = '', timer: number = 1000): void {
		Swal.fire({
			title,
			timer,
			text: description,
			icon: 'success',
			showConfirmButton: false,
		});
	}

	public static error(title: string, description: string = ''): void {
		Swal.fire(title, description, 'error');
	}

	public static info(
		title: string,
		description: string = '',
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): void {
		Swal.fire({
			title,
			text: description,
			showConfirmButton: true,
			showCancelButton: false,
			allowOutsideClick,
			allowEscapeKey,
		});
	}

	public static loading(
		title: string,
		description: string = '',
		timer: number = 500,
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): void {
		Swal.fire({
			title,
			text: description,
			timer,
			didOpen: () => {
				Swal.showLoading();
			},
			allowOutsideClick,
			allowEscapeKey,
		});
	}

	public static confirm(
		title: string,
		description: string = '',
		options: object = {},
	): Promise<any> {
		return Swal.fire(_assignIn({
			title,
			text: description,
			type: 'warning',
			showCancelButton: true,
			cancelButtonText: 'No',
			confirmButtonText: 'Yes',
		}, options));
	}

	public static input(
		title: string,
		description: string = '',
		options: object = {},
	): Promise<any> {
		return Swal.fire(_assignIn({
			title,
			text: description,
			input: 'text',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Accept',
		}, options));
	}

	private translateService: TranslateService;

	constructor(private injector: Injector) {
		setTimeout(() => {
			this.translateService = this.injector.get(TranslateService);
		});
	}

	public translateSuccess(
		title: string,
		description: string = SwalService.empty,
		titleParams?: object,
		descriptionParams?: object,
		timer: number = 1000,
	): void {
		forkJoin(
			this.translateService.get(title, titleParams),
			this.translateService.get(description, descriptionParams),
		).subscribe(([titleTranslated, descriptionTranslated]) => {
			SwalService.success(titleTranslated, descriptionTranslated, timer);
		});
	}

	translateError(title: string, description: string = SwalService.empty, titleParams?: object, descriptionParams?: object): void {
		forkJoin(
			this.translateService.get(title, titleParams),
			this.translateService.get(description, descriptionParams),
		).subscribe(([titleTranslated, descriptionTranslated]) => {
			SwalService.error(titleTranslated, descriptionTranslated);
		});
	}

	public translateInfo(
		title: string,
		description: string = SwalService.empty,
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): void {
		forkJoin(
			this.translateService.get(title),
			this.translateService.get(description),
		).subscribe(([titleTranslated, descriptionTranslated]) => {
			SwalService.info(titleTranslated, descriptionTranslated, allowOutsideClick, allowEscapeKey);
		});
	}

	public translateLoading(
		title: string,
		description: string = SwalService.empty,
		timer: number = 500,
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): void {
		forkJoin(
			this.translateService.get(title),
			this.translateService.get(description),
		).subscribe(([titleTranslated, descriptionTranslated]) => {
			SwalService.loading(titleTranslated, descriptionTranslated, timer, allowOutsideClick, allowEscapeKey);
		});
	}

	public translateConfirm(
		title: string,
		description: string = SwalService.empty,
		titleParams?: object,
		descriptionParams?: object,
		options: object = {},
	): Observable<any> {
		return forkJoin(
			this.translateService.get(title, titleParams),
			this.translateService.get(description, descriptionParams),
		).pipe(flatMap(([titleTranslated, descriptionTranslated]) => {
			return from(SwalService.confirm(titleTranslated, descriptionTranslated, options));
		}));
	}

	public translateInput(
		title: string,
		description: string = SwalService.empty,
		options: object = {},
	): Observable<any> {
		return forkJoin(
			this.translateService.get(title),
			this.translateService.get(description),
		).pipe(flatMap(([titleTranslated, descriptionTranslated]) => {
			return from(SwalService.input(titleTranslated, descriptionTranslated, options));
		}));
	}

	public checkbox(
		title: string,
		description: string = '',
		checkBoxText: string = '',
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): void {
		Swal.fire({
			title,
			allowOutsideClick,
			allowEscapeKey,
			text: description,
			input: 'checkbox',
			showConfirmButton: true,
			showCancelButton: false,
			inputPlaceholder: checkBoxText,
			inputValidator: (result) => {
				return new Promise<string>((resolve, reject) => {
					if (result) {
						resolve(result);
					} else {
						this.translateService.get('errors.required').subscribe((errorMsg) => {
							reject(errorMsg);
						});
					}
				});
			},
		});
	}

	public translateCheckbox(
		title: string,
		description: string = SwalService.empty,
		checkBoxText: string = SwalService.empty,
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): void {
		forkJoin(
			this.translateService.get(title),
			this.translateService.get(description),
			this.translateService.get(checkBoxText),
		).subscribe(([titleTranslated, descriptionTranslated, checkBoxTextTranslated]) => {
			this.checkbox(titleTranslated, descriptionTranslated, checkBoxTextTranslated, allowOutsideClick, allowEscapeKey);
		});
	}

	public select(
		title: string,
		description: string = '',
		selectOptions: { [inputValue: string]: string },
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): Observable<any> {
		return from(Swal.fire({
			title,
			input: 'select',
			inputOptions: selectOptions,
			inputPlaceholder: description,
			showConfirmButton: true,
			showCancelButton: false,
			allowOutsideClick,
			allowEscapeKey,
			inputValidator: (result) => {
				return new Promise<string>((resolve, reject) => {
					if (result) {
						resolve(result);
					} else {
						this.translateService.get('commons.errors.required').subscribe((errorMsg) => {
							reject(errorMsg);
						});
					}
				});
			},
		}).then((result) => {
			return result;
		}));
	}

	public translateSelect(
		title: string,
		description: string = SwalService.empty,
		selectOptions: { [inputValue: string]: string } = {},
		allowOutsideClick: boolean = true,
		allowEscapeKey: boolean = true,
	): Observable<any> {
		return forkJoin(
			this.translateService.get(title),
			this.translateService.get(description))
			.pipe(
				map(([titleTranslated, descriptionTranslated]) => {
					return this.select(titleTranslated, descriptionTranslated, selectOptions, allowOutsideClick, allowEscapeKey);
				}),
				flatMap((selectObservable) => {
					return selectObservable;
				})
			);
	}
}
